/**
 * One capability's priority on its plugin page (optum-agent Feature 87, D10,
 * P3): the local Select with "Use the default" first, and how many of its
 * tasks run now. A platform admin sets it; anyone else reads it.
 */
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { ApiError } from '@optum/api-client/client';
import { setCapabilityPriority } from '@optum/api-client/endpoints/capability-priorities';
import type { CapabilityLevel } from '@optum/api-client/types';

import { useAuth } from '../../auth/AuthContext.js';
import { Field } from '../../ui/Field.js';
import { Select } from '../../ui/Select.js';
import { useToast } from '../../ui/useToast.js';
import { qk } from '../../lib/query-keys.js';
import { LEVEL_LABEL, LEVEL_OPTIONS, isLevel } from '../../lib/priority.js';
import { useCapabilityPriorities } from './useCapabilityPriorities.js';

const USE_DEFAULT = 'default';

export function CapabilityPriorityField({ capabilityId }: { capabilityId: string }) {
  const { isPlatformAdmin } = useAuth();
  const toast = useToast();
  const qc = useQueryClient();
  const view = useCapabilityPriorities([capabilityId]);

  const save = useMutation({
    mutationFn: (level: CapabilityLevel | null) => setCapabilityPriority(capabilityId, level),
    onSuccess: (row) => {
      toast.success(
        row.level === null
          ? `${capabilityId} uses the default priority`
          : `${capabilityId} is ${LEVEL_LABEL[row.level]}`,
      );
      void qc.invalidateQueries({ queryKey: qk.capabilityPriorities() });
    },
    onError: (e) => toast.error('Save failed', e instanceof ApiError ? e.message : String(e)),
  });

  const row = view?.rows[0];
  const id = `priority-${capabilityId}`;
  return (
    <Field
      id={id}
      label="Priority"
      hint="Orders this capability's waiting tasks inside each organization. Applies at the next claim round; no restart."
    >
      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 11 }}>
        <Select
          id={id}
          options={[
            {
              value: USE_DEFAULT,
              label: `Use the default (${view ? LEVEL_LABEL[view.defaultLevel] : '…'})`,
            },
            ...LEVEL_OPTIONS,
          ]}
          value={row?.level ?? USE_DEFAULT}
          onChange={(value) => {
            const next = isLevel(value) ? value : null;
            if (next !== (row?.level ?? null)) save.mutate(next);
          }}
          disabled={!isPlatformAdmin || row === undefined || save.isPending}
          width={240}
        />
        {row ? (
          <span
            className="mono"
            style={{ fontSize: 'var(--text-meta-lg)', color: 'var(--text-muted)' }}
          >
            {row.running} running now
          </span>
        ) : null}
      </div>
    </Field>
  );
}
